import { useMemo, useState } from 'react';
import { useOne, useCreate, useUpdate, useGo, useList } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import { Loader2, Tag } from 'lucide-react';
import { ColorPicker } from '@/components/ui/color-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select';
import { FormHeader, FormStickyActions, FormSpacer } from '@/components/forms';
import { notify } from '@/lib/notify';
import type { TagItem, RequiredChannel } from './types';
import { TagAssignmentsCards } from './components';

interface LanguageItem {
  code: string;
  name: string;
  isActive?: boolean;
}

interface TelegramBotItem {
  id: number;
  name: string;
  username: string | null;
  isActive: boolean;
}

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_COLOR = '#6366f1';

interface TagFormData {
  name: string;
  color: string;
  description: string;
  languages: string[];
  botIds: number[];
  requiredChannels: RequiredChannel[];
  skipEmailOtp: boolean;
  linkModalDismissible: boolean;
}

function buildInitialState(tag?: TagItem): TagFormData {
  if (!tag) {
    return { name: '', color: DEFAULT_COLOR, description: '', languages: [], botIds: [], requiredChannels: [], skipEmailOtp: false, linkModalDismissible: false };
  }
  return {
    name: tag.name,
    color: tag.color,
    description: tag.description ?? '',
    languages: tag.languages ?? [],
    botIds: tag.botIds ?? [],
    requiredChannels: tag.requiredChannels ?? [],
    skipEmailOtp: tag.skipEmailOtp ?? false,
    linkModalDismissible: tag.linkModalDismissible ?? false,
  };
}

const REQUIRED_CHANNEL_OPTIONS: { value: RequiredChannel; label: string }[] = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'TELEGRAM', label: 'Telegram' },
  { value: 'GOOGLE', label: 'Google' },
];

export function TagsForm() {
  const { id } = useParams<{ id: string }>();
  const go = useGo();
  const isEdit = !!id;

  const oneResult = useOne<TagItem>({
    resource: 'tags',
    id: id ? parseInt(id, 10) : undefined,
    queryOptions: { enabled: isEdit },
  });

  const tagData = oneResult.query.data?.data;
  const isLoadingData = oneResult.query.isLoading;

  if (isEdit && isLoadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const formKey = isEdit ? `edit-${tagData?.id ?? 'loading'}` : 'create';

  return (
    <TagsFormFields
      key={formKey}
      isEdit={isEdit}
      id={id}
      initialState={buildInitialState(tagData)}
      onBack={() => go({ to: '/tags' })}
    />
  );
}

interface TagsFormFieldsProps {
  isEdit: boolean;
  id?: string;
  initialState: TagFormData;
  onBack: () => void;
}

function TagsFormFields({ isEdit, id, initialState, onBack }: TagsFormFieldsProps) {
  const go = useGo();
  const [formData, setFormData] = useState<TagFormData>(initialState);
  const [colorError, setColorError] = useState<string | null>(null);

  const createMutation = useCreate();
  const updateMutation = useUpdate();

  const isSubmitting = createMutation.mutation.isPending || updateMutation.mutation.isPending;

  // Языки — из resource 'languages' (синхронизировано с user.list / livestream.show).
  const languagesResult = useList<LanguageItem>({
    resource: 'languages',
    pagination: { mode: 'off' },
    queryOptions: { staleTime: 60_000 },
  });
  const languageOptions = useMemo<MultiSelectOption[]>(
    () =>
      (languagesResult.query.data?.data ?? []).map((l) => ({
        value: l.code,
        label: `${l.code.toUpperCase()} — ${l.name}`,
      })),
    [languagesResult.query.data],
  );

  // Боты — из resource 'bot-tokens'.
  const botsResult = useList<TelegramBotItem>({
    resource: 'bot-tokens',
    pagination: { pageSize: 200, mode: 'server' },
    queryOptions: { staleTime: 60_000 },
  });
  const botOptions = useMemo<MultiSelectOption[]>(
    () =>
      (botsResult.query.data?.data ?? [])
        .filter((b) => b.isActive)
        .map((b) => ({
          value: String(b.id),
          label: b.username ? `${b.name} (@${b.username})` : b.name,
        })),
    [botsResult.query.data],
  );

  const handleChange = <K extends keyof TagFormData>(field: K, value: TagFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'color') {
      setColorError(null);
    }
  };

  const validate = (): boolean => {
    if (!formData.name.trim()) {
      notify.error('Введите название тега');
      return false;
    }
    if (!HEX_REGEX.test(formData.color)) {
      setColorError('Введите корректный hex-цвет (например #6366f1)');
      return false;
    }
    return true;
  };

  const submitData = () => {
    if (!validate()) { return; }

    const values = {
      name: formData.name.trim(),
      color: formData.color,
      description: formData.description.trim() || null,
      languages: formData.languages,
      botIds: formData.botIds,
      requiredChannels: formData.requiredChannels,
      skipEmailOtp: formData.skipEmailOtp,
      linkModalDismissible: formData.linkModalDismissible,
    };

    if (isEdit && id) {
      updateMutation.mutate(
        { resource: 'tags', id: parseInt(id, 10), values },
        {
          onSuccess: () => {
            go({ to: '/tags' });
          },
          onError: (error) => {
            const message = (error as { message?: string }).message ?? 'Ошибка обновления';
            notify.error(message);
          },
        }
      );
    } else {
      createMutation.mutate(
        { resource: 'tags', values },
        {
          onSuccess: () => {
            go({ to: '/tags' });
          },
          onError: (error) => {
            const message = (error as { message?: string }).message ?? 'Ошибка создания';
            notify.error(message);
          },
        }
      );
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitData();
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? 'Редактировать тег' : 'Создать тег'}
        description="Теги для категоризации пользователей"
        sticky
        icon={<Tag className="h-5 w-5" />}
        onBack={onBack}
      />

      <form id="tag-form" onSubmit={handleFormSubmit} className="space-y-6 max-w-lg">
        <Card>
          <CardHeader>
            <CardTitle>Основная информация</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Название *</Label>
              <Input
                id="name"
                placeholder="VIP клиент"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Цвет *</Label>
              <ColorPicker
                id="color"
                value={formData.color}
                onChange={(color) => handleChange('color', color)}
                invalid={!!colorError}
              />
              {colorError && (
                <p className="text-sm text-destructive">{colorError}</p>
              )}
              <p className="text-xs text-muted-foreground">Hex-формат: #rrggbb</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Описание</Label>
              <Textarea
                id="description"
                placeholder="Необязательное описание тега..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Видимость</CardTitle>
            <p className="text-sm text-muted-foreground">
              Где и кому показывать этот тег. Пустые поля = виден везде и всем.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Языки</Label>
              <MultiSelect
                options={languageOptions}
                value={formData.languages}
                onChange={(values: string[]) => handleChange('languages', values)}
                placeholder={
                  languagesResult.query.isLoading
                    ? 'Загрузка языков…'
                    : 'Все языки (по умолчанию)'
                }
                disabled={languagesResult.query.isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Тег показывается пользователю только если его язык интерфейса попал в список.
                Пусто = виден на любом языке.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Боты</Label>
              <MultiSelect
                options={botOptions}
                value={formData.botIds.map(String)}
                onChange={(values: string[]) => handleChange('botIds', values.map((v) => Number(v)))}
                placeholder={
                  botsResult.query.isLoading
                    ? 'Загрузка ботов…'
                    : 'Все боты (по умолчанию)'
                }
                disabled={botsResult.query.isLoading}
              />
              <p className="text-xs text-muted-foreground">
                В каких telegram-ботах виден тег (для пользователей, которые туда зашли).
                Пусто = виден во всех ботах.
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Привязка аккаунта</CardTitle>
            <p className="text-sm text-muted-foreground">
              Требуемые способы авторизации для пользователей с этим тегом.
              Пусто = нет дополнительных требований.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Требовать привязку аккаунта</Label>
              <div className="flex flex-col gap-3">
                {REQUIRED_CHANNEL_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`channel-${option.value}`}
                      checked={formData.requiredChannels.includes(option.value)}
                      onCheckedChange={(checked) => {
                        const next = checked === true
                          ? [...formData.requiredChannels, option.value]
                          : formData.requiredChannels.filter((c) => c !== option.value);
                        // Если EMAIL снят — сбрасываем skipEmailOtp;
                        // если каналов не осталось — сбрасываем и linkModalDismissible.
                        setFormData((prev) => ({
                          ...prev,
                          requiredChannels: next,
                          skipEmailOtp: option.value === 'EMAIL' && checked !== true ? false : prev.skipEmailOtp,
                          linkModalDismissible: next.length === 0 ? false : prev.linkModalDismissible,
                        }));
                      }}
                    />
                    <Label htmlFor={`channel-${option.value}`} className="font-normal cursor-pointer">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="skip-email-otp"
                checked={formData.skipEmailOtp}
                disabled={!formData.requiredChannels.includes('EMAIL')}
                onCheckedChange={(checked) => handleChange('skipEmailOtp', checked === true)}
              />
              <div className="flex flex-col gap-0.5">
                <Label
                  htmlFor="skip-email-otp"
                  className={`font-normal cursor-pointer ${!formData.requiredChannels.includes('EMAIL') ? 'text-muted-foreground' : ''}`}
                >
                  Не требовать OTP для подтверждения почты
                </Label>
                <p className="text-xs text-muted-foreground">
                  Активно только если выбран Email. При снятии флага — вернётся OTP-подтверждение.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="link-modal-dismissible"
                checked={formData.linkModalDismissible}
                disabled={formData.requiredChannels.length === 0}
                onCheckedChange={(checked) => handleChange('linkModalDismissible', checked === true)}
              />
              <div className="flex flex-col gap-0.5">
                <Label
                  htmlFor="link-modal-dismissible"
                  className={`font-normal cursor-pointer ${formData.requiredChannels.length === 0 ? 'text-muted-foreground' : ''}`}
                >
                  Разрешить «Больше не показывать»
                </Label>
                <p className="text-xs text-muted-foreground">
                  Пользователь сможет навсегда скрыть модалку привязки чекбоксом.
                  Если у пользователя несколько требующих тегов — скрытие доступно,
                  только когда его разрешают все такие теги.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>

      {isEdit && id && (
        <section className="space-y-3 pt-4 border-t">
          <div>
            <h3 className="text-base font-semibold">Где используется тег</h3>
            <p className="text-sm text-muted-foreground">
              Сущности, к которым прикреплён этот тег. Клик по карточке — переход к редактированию.
            </p>
          </div>
          <TagAssignmentsCards tagId={Number(id)} />
        </section>
      )}

      <FormSpacer />

      <FormStickyActions
        primaryLabel={isEdit ? 'Сохранить' : 'Создать'}
        secondaryLabel="Отмена"
        onPrimary={submitData}
        onSecondary={onBack}
        isPending={isSubmitting}
        pendingLabel={isEdit ? 'Сохранение...' : 'Создание...'}
      />
    </div>
  );
}
